import React from "react";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";

import { IonLabel, IonButton, IonIcon } from "@ionic/react";
import { ErrorMessage } from "@hookform/error-message";

import { useMutation } from "@apollo/client";
import { UPLOAD_IMAGE_MUTATION } from "../../graphql/mutations/common.mutation";

import { camera } from "ionicons/icons";

import "./fileupload.styles.css";
import { getSignedUrl } from "../../lib/utils/common.utils";

interface IInput {
  form?: {
    register: any;
    errors: any;
    registerName: string;
    pattern?: {
      value: RegExp | string;
      message: string;
    };
    requiredMessage?: string | false;
    setValue: any;
    validate?: any;
  };

  formLess?: {
    files: any[];
    setFiles: any;
  };

  dragAndDrop?: boolean;
  fieldName?: string;
  showImage?: boolean;
  defaultFiles?: string[] | null;
  accept?: string;
}

const FileUpload = (props: IInput) => {
  const [signedFiles, setSignedFiles] = React.useState<any[]>([]);

  const [updateImage] = useMutation(UPLOAD_IMAGE_MUTATION);

  const uploadFiles = (files: any[]) => {
    for (const file of files) {
      const exists = (props.formLess?.files ?? signedFiles).find(
        (sf: any) => sf.original === file.name
      );
      if (exists) continue;

      updateImage({
        variables: {
          file,
        },
      }).then((res) => {
        toast.success(`${file.name} - File caricato con successo`);

        // Update file field for preview
        getSignedUrl(res.data.uploadFile).then((_signedUrl: string) => {
          const signedUrl = _signedUrl ?? URL.createObjectURL(file);

          if (props.form) {
            setSignedFiles((prevState) => {
              const newList = [
                ...prevState,
                {
                  preview: signedUrl,
                  original: res.data.uploadFile,
                  localName: file.name,
                },
              ];

              props.form?.setValue(
                props.form.registerName,
                newList.map((f: any) => f.original)
              );

              return newList;
            });
          } else {
            props.formLess?.setFiles((prevState: any[]) => [
              ...prevState,
              {
                preview: signedUrl,
                original: res.data.uploadFile,
                localName: file.name,
              },
            ]);
          }
        });
      });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles) => {
      uploadFiles(acceptedFiles);
    },
  });

  const onFileChange = (fileChanged: FileList | null) => {
    if (!fileChanged) return;
    uploadFiles(Array.from(fileChanged));
  };

  // Formless file upload won't have default files, so can ignore this
  React.useEffect(() => {
    if (!props.defaultFiles) return;
    for (const defaultF of props.defaultFiles) {
      const exists = signedFiles.find((sf: any) => sf.original === defaultF);
      if (exists) continue;

      getSignedUrl(defaultF).then((signedUrl: string) => {
        setSignedFiles((prevState) => {
          const allFiles = [
            ...prevState,
            { original: defaultF, preview: signedUrl, localName: null },
          ];
          if (props.formLess) props.formLess.setFiles(allFiles);
          return allFiles;
        });
      });
    }
  }, [props.defaultFiles]);

  const openFileDialog = () => {
    (document as any).getElementById("fileupload-input-mine").click();
  };

  const removeFile = (file: any) => {
    let otherFiles: any[];

    if (props.form) {
      otherFiles = signedFiles.filter((f: any) => f.original !== file.original);
    } else {
      otherFiles =
        props.formLess?.files.filter(
          (f: any) => f.original !== file.original
        ) ?? [];
    }

    setSignedFiles(otherFiles);

    if (props.form) {
      props.form.setValue(
        props.form.registerName,
        otherFiles.map((f: any) => f.original)
      );
    } else {
      props.formLess?.setFiles(otherFiles);
    }
  };

  const filesForPreview =
    (props.form ? signedFiles : props.formLess?.files) ?? [];

  return (
    <div className="app-fileupload-wrapper">
      {props.fieldName && (
        <IonLabel className="app-fileupload-label">{props.fieldName}</IonLabel>
      )}

      <input
        className="app-fileupload-field"
        type="file"
        id="fileupload-input-mine"
        onChange={(ev) => onFileChange(ev.target.files)}
        accept={props.accept}
        multiple
      />

      {props.dragAndDrop ? (
        <div className="attesa-service-drag-and-drop">
          <div className="attesa-dnd-border">
            <div {...getRootProps()} className="attesa-full-block">
              <input {...getInputProps()} className="attesa-full-block" />
              <svg
                width="60"
                height="60"
                viewBox="0 0 60 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M54.8438 60C57.6914 60 60 57.6914 60 54.8438V42.6562C60 39.8086 57.6914 37.5 54.8438 37.5H43.1953L49.8047 30.8906C52.7461 27.9375 50.6602 22.8867 46.4883 22.8867H40.3125V4.6875C40.3125 2.09766 38.2148 0 35.625 0H24.375C21.7852 0 19.6875 2.09766 19.6875 4.6875V22.8867H13.5117C9.33984 22.8867 7.24219 27.9375 10.1953 30.8906L16.8047 37.5H5.15625C2.30859 37.5 0 39.8086 0 42.6562V54.8438C0 57.6914 2.30859 60 5.15625 60H54.8438ZM43.125 48.2812C41.8359 48.2812 40.7812 49.3359 40.7812 50.625C40.7812 51.9141 41.8359 52.9688 43.125 52.9688C44.4141 52.9688 45.4688 51.9141 45.4688 50.625C45.4688 49.3359 44.4141 48.2812 43.125 48.2812ZM52.9688 50.625C52.9688 51.9141 51.9141 52.9688 50.625 52.9688C49.3359 52.9688 48.2812 51.9141 48.2812 50.625C48.2812 49.3359 49.3359 48.2812 50.625 48.2812C51.9141 48.2812 52.9688 49.3359 52.9688 50.625ZM29.332 44.7305C29.6953 45.0938 30.293 45.0938 30.6562 44.7305L47.1445 28.2422C47.7305 27.6562 47.3203 26.6367 46.4766 26.6367H36.5625V4.6875C36.5625 4.17188 36.1406 3.75 35.625 3.75H24.375C23.8594 3.75 23.4375 4.17188 23.4375 4.6875V26.6367H13.5117C12.6797 26.6367 12.2578 27.6445 12.8438 28.2422L29.332 44.7305ZM54.8438 41.25C55.6172 41.25 56.25 41.8828 56.25 42.6562V54.8438C56.25 55.6172 55.6172 56.25 54.8438 56.25H5.15625C4.38281 56.25 3.75 55.6172 3.75 54.8438V42.6562C3.75 41.8828 4.38281 41.25 5.15625 41.25H20.5547L26.6836 47.3789C28.5117 49.207 31.4883 49.207 33.3164 47.3789L39.4453 41.25H54.8438Z"
                  fill="#588CD3"
                />
              </svg>
              <p>Sfoglia o trascina qui i tuoi file</p>
            </div>
          </div>
        </div>
      ) : (
        <IonButton onClick={openFileDialog}>
          <IonIcon slot="start" icon={camera} /> Upload
        </IonButton>
      )}

      <input
        className="app-fileupload-field"
        {...(props.form && {
          ...props.form?.register(props.form?.registerName, {
            required: props.form?.requiredMessage,
            validate: props.form?.validate,
            pattern: {
              value: props.form?.pattern && props.form?.pattern.value,
              message: props.form?.pattern && props.form?.pattern.message,
            },
          }),
        })}
      />

      {props.form && (
        <ErrorMessage
          errors={props.form.errors}
          name={props.form.registerName}
          as={"span"}
        />
      )}

      {props.showImage !== false && (
        <FileUploadPreview files={filesForPreview} removeFile={removeFile} />
      )}
    </div>
  );
};

interface IFilePreviewProps {
  files: any[];
  removeFile: (file: any) => any;
  canRemove?: boolean;
}
export function FileUploadPreview(props: IFilePreviewProps) {
  const canRemove = props.canRemove ?? true;

  const openFile = (file: any) => {
    window.open(file.preview, "_blank")?.focus();
  };

  return (
    <div className="app-fileupload-flex">
      {props.files.map((file: any, index: number) => (
        <div key={index} className="app-fileupload-image-box">
          {file.original.includes(".pdf") ? (
            <div
              className="app-fileupload-image"
              onClick={() => openFile(file)}
            >
              PDF
            </div>
          ) : (
            <img
              src={file.preview}
              alt={file.preview}
              className="app-fileupload-image"
              onClick={() => openFile(file)}
            />
          )}

          {canRemove && (
            <div
              className="app-fileupload-image-remove"
              onClick={() => props.removeFile(file)}
            >
              x
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

FileUpload.defaultProps = {
  accept: "image/*, application/pdf",
};

export default FileUpload;
